import {
  Button,
  ColumnInfoBlock,
  ProfileImagePlaceholder,
  SocialLinkButton,
} from 'components';
import { FieldEditIcon } from 'components/ColumnInfoBlock/styled';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import { phoneData } from 'configs/phones';
import { socialNetwork } from 'configs/socialNetworks';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Linkify from 'react-linkify';
import { useSelector } from 'react-redux';
import { FIELDS } from 'sidebars/EditProfile/form';
import { Redirect } from 'static';
import { capMap, getDate, getNameFromEmail } from 'utils';
import { canChangeCognitoCredentials } from 'utils/auth';
import { getProfileNameCapital } from 'utils/common';

import {
  getAuthUser,
  getFullAuthUser,
  getMyId,
} from '../../../store/selectors/auth';
import {
  AvatarEditIcon,
  AvatarEditOverlay,
  AvatarWrapper,
  BtnTitle,
  ColumWrapper,
  Container,
  Description,
  DescriptionContainer,
  HeadInfoContainer,
  HeadInfoContainerWrapper,
  LinkEmail,
  MainInfoBlock,
  ProfileName,
  ProfileWrapper,
  SocialInfoContainer,
  SocialLinks,
  Title,
  UpdateBtnWrapper,
  UserImage,
  WrapperIcon,
} from './styled';

const Content = ({ openEditSidebar, userRole }) => {
  const { t } = useTranslation();
  const user = useSelector(getFullAuthUser);
  const [socialLinks, setSocialLinks] = useState([]);
  const profile = useSelector(getAuthUser);
  const authId = useSelector(getMyId);
  const itsMe = authId === profile.userId;
  const socialData = useMemo(() => {
    return socialNetwork
      .map((li) => {
        if (![...(profile?.links || [])].some(({ label }) => label === li)) {
          return { label: li, href: '' };
        }
      })
      .filter(Boolean);
  }, [socialNetwork, profile?.links]);

  useEffect(() => {
    setSocialLinks(socialData);
  }, [socialData]);
  const canChangeCredentials = canChangeCognitoCredentials(user?.idToken);
  const phoneNumberTypes = [
    'privatePhoneNumber',
    'officePhoneNumber',
    'mobilePhoneNumber',
  ].reverse();
  const extraStyle = {
    textTransform: 'capitalize',
  };
  const phoneNumbers = phoneNumberTypes.map((label) => ({
    label,
    value: user?.profile.phones?.find((p) => p.label === label)?.value || '-',
  }));

  return (
    <Container>
      <HeadInfoContainerWrapper>
        <HeadInfoContainer>
          <MainInfoBlock left={profile.description}>
            <SocialInfoContainer>
              <AvatarWrapper onClick={openEditSidebar}>
                {profile.logo ? (
                  <UserImage src={profile.logo} />
                ) : (
                  <ProfileImagePlaceholder />
                )}
                <AvatarEditOverlay>
                  <AvatarEditIcon title="Edit profile image" />
                </AvatarEditOverlay>
              </AvatarWrapper>
            </SocialInfoContainer>
            <SocialInfoContainer>
              <ProfileName>
                {getProfileNameCapital({
                  firstName: profile?.firstName,
                  lastName: profile?.lastName,
                }) || getNameFromEmail(profile?.email)}
              </ProfileName>
              <SocialLinks>
                {capMap(
                  ({ value, label }) => (
                    <SocialLinkButton
                      key={value}
                      openEditSidebar={openEditSidebar}
                      href={value}
                      socialNetwork={label}
                      width={value ? '18px' : '14px'}
                      userRole={userRole}
                      itsMe={itsMe}
                    />
                  ),
                  [...(profile?.links || []), ...(socialLinks || [])],
                )}
              </SocialLinks>
            </SocialInfoContainer>
          </MainInfoBlock>
          {/* {profile.description && ( */}
          <DescriptionContainer hasDescription={profile.description}>
            {profile.description ? (
              <Linkify
                componentDecorator={(decoratedHref, decoratedText, key) => (
                  <a target="blank" href={decoratedHref} key={key}>
                    {decoratedText}
                  </a>
                )}
              >
                <Description>
                  {profile.description}
                  <WrapperIcon>
                    <FieldEditIcon
                      title="Edit"
                      onClick={() => openEditSidebar(FIELDS.ABOUT)}
                    />
                  </WrapperIcon>
                </Description>
              </Linkify>
            ) : (
              <ColumnInfoBlock
                label={t('common.aboutMe')}
                text={profile.description}
                name={FIELDS.ABOUT}
                isDescription={true}
              />
            )}
          </DescriptionContainer>
          {/* )} */}
        </HeadInfoContainer>
        {/* <UpdateBtnWrapper>
          <Button onClick={openEditSidebar} style={btnExtraStyle}>
            <>
              <Redirect />
              <BtnTitle>{t('profile.profileEditBtn')}</BtnTitle>
            </>
          </Button>
        </UpdateBtnWrapper> */}
      </HeadInfoContainerWrapper>
      <ProfileWrapper>
        <Title>
          <p>{t('profile.basicInformation')}</p>
        </Title>
        <ColumWrapper>
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.firstName')}
            text={
              getProfileNameCapital({
                firstName: user?.profile?.firstName,
              }) as string
            }
            name={FIELDS.FIRST_NAME}
          />
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.lastName')}
            name={FIELDS.LAST_NAME}
            text={
              getProfileNameCapital({
                lastName: user?.profile?.lastName,
              }) as string
            }
          />
        </ColumWrapper>
        <ColumWrapper>
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.profileName')}
            text={user.profile.profileName}
            name={FIELDS.PROFILE_NAME}
          />
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.dateOfBirth')}
            text={getDate(user.profile.dob)}
            name={FIELDS.DATE_OF_BIRTH}
          />
        </ColumWrapper>
        <ColumWrapper>
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.freeText1') || 'Free Text 1'}
            text={user.profile.freeText1}
            name={FIELDS.FREE_TEXT_1}
          />
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.freeText2') || 'Free Text 2'}
            text={user.profile.freeText2}
            name={FIELDS.FREE_TEXT_2}
          />
        </ColumWrapper>
        <Title>
          <p style={{ marginTop: '32px' }}>{t('profile.contactInformation')}</p>
        </Title>
        <ColumWrapper>
          <ColumnInfoBlock
            addMaxWidth
            label={
              <>
                {t('profile.email')}{' '}
                {canChangeCredentials && (
                  <LinkEmail
                    to={`${ROUTES.PROFILE}/${PROFILE_ROUTES.SETTINGS}#update-email`}
                  >
                    {t('profile.linkChangeEmail')}
                  </LinkEmail>
                )}
              </>
            }
            text={user.profile.email}
            preventEdit={!canChangeCredentials}
          />
          {capMap(
            ({ value, label }, index) => (
              <ColumnInfoBlock
                addMaxWidth
                key={label}
                label={t(`profile.${label}`)}
                text={value}
                name={FIELDS[phoneData[index]]}
              />
            ),
            phoneNumbers,
          )}
        </ColumWrapper>
        <Title>
          <p style={{ marginTop: '32px' }}>{t('profile.addressInformation')}</p>
        </Title>
        <ColumWrapper>
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.street')}
            text={user.profile.street}
            name={FIELDS.STREET}
            style={extraStyle}
          />
          <ColumnInfoBlock
            label={t('profile.region')}
            text={user.profile.region}
            name={FIELDS.REGION}
            addMaxWidth
            style={extraStyle}
          />
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.postalCode')}
            text={user.profile.postalCode}
            name={FIELDS.POSTAL_CODE}
          />
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.city')}
            name={FIELDS.CITY}
            text={user.profile.city}
            style={extraStyle}
          />
          <ColumnInfoBlock
            addMaxWidth
            label={t('profile.country')}
            text={user.profile.country}
            name={FIELDS.COUNTRY}
          />
        </ColumWrapper>
      </ProfileWrapper>
    </Container>
  );
};

export default Content;
